import { IObedienceScore, IRallyObedienceScore } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";

import { isNonQualifyingResult } from "../helpers/result-type";

// Non-regular (Pairs/Teams) rally levels start at 200 points
const NON_REGULAR_RALLY_LEVELS = new Set<string>([
  Constants.dog_class_element_level.pairs,
  Constants.dog_class_element_level.novice_pairs,
  Constants.dog_class_element_level.advanced_pairs,
  Constants.dog_class_element_level.excellent_pairs,
  Constants.dog_class_element_level.team,
  Constants.dog_class_element_level.novice_teams,
  Constants.dog_class_element_level.advanced_teams,
]);

/**
 * Calculate displayed score for obedience and rally obedience.
 * Ported verbatim from web ScoresGrid/utils/obedience.ts so all surfaces agree.
 */
export const calculateObedienceScore = (
  score: IObedienceScore | IRallyObedienceScore | undefined,
  showType: string,
  level?: string
): string | number => {
  if (!score || !score.result_type) {
    return "";
  }

  // Show N/A for non-qualifying results
  if (isNonQualifyingResult(score.result_type)) {
    return "N/A";
  }

  // Calculate score for non-regular classes (Pairs) - 200 - deductions for Rally
  if (score.result_type === Constants.score_result_type.non_regular) {
    let deductions = 0;
    if (score.total_deductions_only) {
      deductions = score.total_deductions ?? 0;
    } else if (score.element_deductions && score.element_deductions.length > 0) {
      deductions = score.element_deductions.reduce(
        (sum: number, item: any) =>
          sum + parseFloat(item.element_deductions ?? "0"),
        0
      );
    } else {
      return ""; // No deductions entered yet
    }
    const finalScore = 200 - deductions;
    return Math.max(finalScore, 0);
  }

  // Only calculate for qualified / not-qualified results
  if (
    score.result_type !== Constants.score_result_type.qualified &&
    score.result_type !== Constants.score_result_type.not_qualified
  ) {
    return "N/A";
  }

  // For C-WAGS Obedience and C-WAGS Rally (any show using total_score directly)
  const scoreWithTotal = score as any;
  if (
    scoreWithTotal.total_score !== undefined &&
    scoreWithTotal.total_score !== null
  ) {
    return scoreWithTotal.total_score;
  }

  // If using deductions system, check if any deductions exist
  let deductions = 0;
  if (score.total_deductions_only) {
    if (
      score.total_deductions !== undefined &&
      score.total_deductions !== null
    ) {
      deductions = score.total_deductions;
    } else {
      return "";
    }
  } else {
    if (!score.element_deductions || score.element_deductions.length === 0) {
      return "";
    }
    deductions = score.element_deductions.reduce(
      (sum: number, item: any) =>
        sum + parseFloat(item.element_deductions ?? "0"),
      0
    );
  }

  // Pairs/Teams rally start at 200 (non-regular), regular rally at 100, obedience at 200
  const isNonRegularRallyLevel = level && NON_REGULAR_RALLY_LEVELS.has(level);
  const baseScore =
    showType === Constants.show_type.obedience || isNonRegularRallyLevel
      ? 200
      : 100;
  const finalScore = baseScore - deductions;

  return Math.max(finalScore, 0);
};
